import { classNames } from "../../../../lib/classNames"
import { Icon, IconProps, IconSize, IconSizes } from "../../../Icon"
import {
  Button,
  ButtonMode,
  ButtonProps,
  ButtonSize,
  NonTonalButtonMode,
} from "../Button"

export interface IconButtonProps
  extends Omit<
      ButtonProps,
      "children" | "leftIcon" | "rightIcon" | "mode" | "tone"
    >,
    Pick<IconProps, "icon"> {
  // IconButton has no `tone` axis, so it does not expose the Tonal mode.
  mode?: NonTonalButtonMode
  iconClassName?: string
}

export function IconButton({
  icon,
  mode = ButtonMode.Ghost,
  size = ButtonSize.Small,
  selected,
  className,
  iconClassName,
  ...otherProps
}: IconButtonProps) {
  return (
    <Button
      size={size}
      mode={mode}
      selected={selected}
      className={classNames("p-0", buttonSizeClassName[size], className)}
      {...otherProps}
    >
      <Icon
        className={classNames(
          iconColorClassName[mode],
          selected && selectedIconColorClassName,
          iconClassName
        )}
        icon={icon}
        size={iconSize[size]}
      />
    </Button>
  )
}

const buttonSizeClassName: Record<ButtonSize, string> = {
  [ButtonSize.ExtraSmall]: "size-5",
  [ButtonSize.Small]: "size-7",
  [ButtonSize.Default]: "size-10",
  [ButtonSize.Medium]: "size-8",
}

const iconColorClassName: Record<NonTonalButtonMode, string> = {
  [ButtonMode.Primary]:
    "text-v2-foreground-inverse group-disabled:text-v2-foreground-inverse-disabled",
  [ButtonMode.Secondary]:
    "text-v2-foreground-default group-disabled:text-v2-foreground-default-disabled",
  [ButtonMode.Ghost]:
    "text-v2-foreground-default group-disabled:text-v2-foreground-default-disabled",
  [ButtonMode.Destructive]:
    "text-v2-foreground-danger group-hover:text-v2-foreground-inverse group-active:text-v2-foreground-inverse group-disabled:text-v2-foreground-danger-disabled",
  [ButtonMode.Violet]:
    "color-icon-accent-violet group-disabled:color-icon-disabled",
  [ButtonMode.Link]:
    "text-v2-foreground-primary group-hover:text-v2-foreground-primary-strong group-disabled:text-v2-foreground-primary-disabled",
}

const selectedIconColorClassName =
  "text-v2-foreground-active group-hover:text-v2-foreground-active group-disabled:text-v2-foreground-default-disabled"

const iconSize: Record<ButtonSize, IconSize> = {
  [ButtonSize.ExtraSmall]: IconSizes.ExtraSmall,
  [ButtonSize.Small]: IconSizes.ExtraSmall,
  [ButtonSize.Medium]: IconSizes.Small,
  [ButtonSize.Default]: IconSizes.Medium,
}
